import { useGetDashboardAssetsQuery } from '@api/dashboard/dashboardEndpoints'
import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import {
  AcquisitionData,
  BookValueData,
  DashboardFilterField,
} from './DashboardFilter.types'

type AssetChartDatum = {
  name: string
  Active: number
  DisposedAndPending: number
}

interface IDashboardFiltersHook {
  // Select and MultiSelect
  getMultiSelectValue: (index: number) => string[]
  handleMultiSelectChange: (list: string[], index: number) => void
  handleSelectAmountChange: (value: { label: string; value: string }, index: number) => void
  getSelectValue: (index: number) => { label: string; value: string }

  // Form methods
  methods: any
  handleDashboardFiltersSubmit: (data: any) => void

  // Assets
  assetCount: number
  filteredAssetsNumber: AssetChartDatum[]
  isFetchingDashboardAssets: boolean
  isSuccessGettingDashboardAssets: boolean

  // Acquisition Data
  acquisitionData: AcquisitionData[]
  totalAcquisition: number

  // Book Value Data
  bookValueData: BookValueData[]
  totalBookValue: number

  // Applied information
  appliedCategoryList: string[]
  isSingleCategoryApplied: boolean
  hasPendingChanges: boolean
}

export function nFormatter(num, digits) {
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
  ]
  const regexp = /\.0+$|(?<=\.[0-9]*[1-9])0+$/
  const item = [...lookup].reverse().find(item => num >= item.value)
  return item ? (num / item.value).toFixed(digits).replace(regexp, '') + item.symbol : '0'
}

const cloneFilters = (filters: DashboardFilterField[]): DashboardFilterField[] =>
  filters.map(filter => ({
    ...filter,
    List: filter.List ? [...filter.List] : filter.List,
  }))

interface UseDashboardFiltersProps {
  filterState: DashboardFilterField[]
  setFilterState: Dispatch<SetStateAction<DashboardFilterField[]>>
}

export default function useDashboardFilters({
  filterState,
  setFilterState,
}: UseDashboardFiltersProps): IDashboardFiltersHook {
  const assetCompanyID = useSelector<any>(state => state.company) as number

  // Fetch filters different from the state filters to avoid re-rendering
  const [fetchFilters, setFetchFilters] = useState(() => cloneFilters(filterState))

  // For the multi select component
  const getMultiSelectValue = (index: number): string[] => {
    if (!filterState[index].List || filterState[index].List.length === 0) {
      setFilterState(prevState => {
        const newState = [...prevState]
        newState[index] = { ...newState[index], List: ['All'] }
        setValueDashboardFilters(`filterState.${index}.List`, ['All'])
        return newState
      })
      return ['All']
    }

    setValueDashboardFilters(`filterState.${index}.List`, filterState[index].List)
    return filterState[index].List
  }

  const handleMultiSelectChange = (list: string[], index: number): void => {
    setFilterState(prevState => {
      const newState = prevState.map((item, i) => {
        if (i === index) {
          const nextList =
            list.includes('All') && list.length === 1
              ? ['All']
              : list.filter(item => item !== 'All')
          return { ...item, List: nextList }
        }
        return item
      })

      setValueDashboardFilters(`filterState.${index}.List`, newState[index].List)

      return newState
    })
  }

  // For the select component
  const getSelectValue = (index: number): { label: string; value: string } => {
    // get the value, and format it to be used in the select component
    const value = filterState[index].Amount ? String(filterState[index].Amount) : '4'
    const label = filterState[index].Label ? filterState[index].Label : 'Last 4 Quarters'

    setValueDashboardFilters(`filterState.${index}.Amount`, value)
    setValueDashboardFilters(`filterState.${index}.Label`, label)

    return { label, value }
  }

  const handleSelectAmountChange = (
    value: { label: string; value: string },
    index: number
  ): void => {
    setFilterState(prevState => {
      const newState = prevState.map((item, i) => {
        if (i === index) {
          return { ...item, Amount: value.value, Label: value.label }
        }
        return item
      })

      setValueDashboardFilters(`filterState.${index}.Amount`, value.value)
      setValueDashboardFilters(`filterState.${index}.Label`, value.label)

      return newState
    })

    setFetchFilters(prevFilters =>
      prevFilters.map(filter =>
        filter.Name === 'Quarters'
          ? { ...filter, Amount: value.value, Label: value.label }
          : filter
      )
    )
  }

  // Form methods
  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      filterState,
    },
  })

  // Set the form values
  const { setValue: setValueDashboardFilters } = methods

  const appliedCategoryFilter = fetchFilters.find(filter => filter.Name === 'AS_CAT_ID')
  const appliedCategoryList = appliedCategoryFilter?.List?.length
    ? [...appliedCategoryFilter.List]
    : ['All']
  const appliedIsAllCategories = appliedCategoryList.includes('All')
  const isSingleCategoryApplied =
    appliedCategoryList.length === 1 && !appliedIsAllCategories

  const hasPendingChanges = useMemo(() => {
    return JSON.stringify(filterState) !== JSON.stringify(fetchFilters)
  }, [filterState, fetchFilters])

  // Get the data from the API
  const {
    data: dashboardAssets,
    isFetching: isFetchingDashboardAssets,
    isSuccess: isSuccessGettingDashboardAssets,
  } = useGetDashboardAssetsQuery({ assetCompanyID, filter: fetchFilters })

  // Submit the form
  const handleDashboardFiltersSubmit = async (data: any): Promise<void> => {
    try {
      setFetchFilters(cloneFilters(data.filterState))
    } catch (error) {
      console.log(error)
    }
  }

  const assetTrendData: AssetChartDatum[] =
    dashboardAssets?.AssetTrend?.map(value => ({
      name: value.Quarter,
      Active: value.Active,
      DisposedAndPending: value.DisposedAndPending,
    })) ?? []

  const categorySnapshotData: AssetChartDatum[] =
    dashboardAssets?.CategorySnapshot?.map(value => ({
      name: value.CategoryName,
      Active: value.Active,
      DisposedAndPending: value.DisposedAndPending,
    })) ?? []

  const acquisitionData: AcquisitionData[] =
    dashboardAssets?.AcquisitionByQuarter?.map(value => ({
      name: value.Quarter,
      Active: value.Active,
      Disposed: value.Disposed,
      Totals: value.Totals,
    })) ?? []

  const bookValueData: BookValueData[] =
    dashboardAssets?.BookValueByQuarter?.map(value => ({
      name: value.Quarter,
      Acquisition: value.Acquisition,
      LTDDepreciation: value.LTDDepreciation,
      BookValue: value.BookValue,
    })) ?? []

  const totalAcquisition = dashboardAssets?.Totals?.TotalAcquisition ?? 0
  const totalBookValue = dashboardAssets?.Totals?.TotalBookValue ?? 0
  const assetCount = dashboardAssets?.Totals?.AssetCount ?? 0
  const filteredAssetsNumber: AssetChartDatum[] = isSingleCategoryApplied
    ? assetTrendData
    : categorySnapshotData

  return {
    // Select and MultiSelect
    getMultiSelectValue,
    handleMultiSelectChange,
    handleSelectAmountChange,
    getSelectValue,

    // Form methods
    methods,
    handleDashboardFiltersSubmit,

    // Assets
    assetCount,
    filteredAssetsNumber,
    isFetchingDashboardAssets,
    isSuccessGettingDashboardAssets,

    // Acquisition Data
    acquisitionData,
    totalAcquisition,

    // Book Value Data
    bookValueData,
    totalBookValue,
    appliedCategoryList,
    isSingleCategoryApplied,
    hasPendingChanges,
  }
}
